/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, call, put, race, select, take } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Asset,
  AssetMediaType,
  AssetsApi,
  Constants,
  Ducks,
  EntryPoint,
  Episode,
  FileFormatToFileTypeMap,
  HideUnsubscribed,
  ParentalControlSettings,
  Program,
  Recording,
  SearchUtils,
  Series,
  TimeUtils,
  AssetUtils,
} from '@wit/vtv-x-foundation-lib';
import AppConfig from '../../../../services/AppConfig';
import { Selectors as PpvSelectors } from '../entitlements/assets/entitledAssets';
import { Selectors as RecordingsSelectors } from '../recordings/singleRecordings';

export interface SeasonsData {
  episodesBySeason: Record<number, Asset[]>;
  episodesClones: Record<string, Asset[]>;
}

interface RepresentativeEpisodesResult {
  episodesBySeason: Map<number, Asset[]>;
  episodesClones: Record<string, Asset[]>;
}

const ASSET_RELEVANCY_ORDER: AssetMediaType[] = [
  AssetMediaType.EPISODE,
  AssetMediaType.MOVIE,
  AssetMediaType.EPG,
  AssetMediaType.RECORDING,
];

function getSeasonNumbers(seasonsData: SeasonsData | undefined): number[] {
  if (seasonsData) {
    const keys: string[] = Object.keys(seasonsData.episodesBySeason);
    const seasonNumbers = keys.map((key) => parseInt(key, 10));
    return seasonNumbers.filter((number) => !Number.isNaN(number));
  }
  return [];
}

function* getSeriesAssetRecordings(seriesId: string): SagaIterator<Asset[]> {
  const assets: Recording[] = yield select(RecordingsSelectors.getRecordings);
  return assets.filter((asset) => asset.seriesId === seriesId);
}

function* getSeriesAssetPurchases(seriesId: string): SagaIterator<Asset[]> {
  const assets: Asset[] = yield select(PpvSelectors.getAssets);
  return assets.filter((asset) => AssetUtils.isEpisode(asset) && asset.seriesId === seriesId);
}

function* searchSeriesAssets(entryAsset: Asset): SagaIterator {
  const mediaTypes = [AssetMediaType.EPG, AssetMediaType.MOVIE, AssetMediaType.EPISODE];
  const ksql = SearchUtils.createSeriesFilter(entryAsset, mediaTypes);

  if (ksql === '') {
    return [];
  }

  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  const result: Asset[] = yield Ducks.Pagination.callCompletedPaginatedApi(
    `series_assets_${(entryAsset as Series).seriesId}`,
    Constants.DEFAULT_API_PAGE_SIZE,
    AssetsApi.assetSearch,
    ksql,
    filesFormat,
    parentalControlSettings,
    mediaTypes,
    undefined,
    HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY],
    undefined,
    EntryPoint.SEASONS
  );

  return result;
}

function* getSeriesAssets(entryAsset: Asset, entryPoint?: EntryPoint): SagaIterator {
  // seggregated by cache
  switch (entryPoint) {
    case EntryPoint.RECORDINGS:
    case EntryPoint.SCHEDULED_RECORDINGS:
      return yield call(getSeriesAssetRecordings, (entryAsset as Series).seriesId);
    case EntryPoint.PURCHASES:
      return yield call(getSeriesAssetPurchases, (entryAsset as Series).seriesId);
    default:
      break;
  }

  // aggregated search + recordings
  const [search, recordings] = yield all([
    call(searchSeriesAssets, entryAsset),
    call(getSeriesAssetRecordings, (entryAsset as Series).seriesId),
  ]);

  return [...search, ...recordings];
}

function groupAssetsBySeasonsAndEpisodes(assets: Asset[]) {
  // season map are indexed by the distinct season number values
  // the inner map will have the assets with the same episode number
  const seasonsEpisodesMap = new Map<number, Map<number, Asset[]>>();

  let seasonNumber;
  let episodeNumber;
  let seasonEpisodesMap;
  let episodesArray: Asset[] | undefined;

  for (let i = 0; i < assets.length; i += 1) {
    seasonNumber = (assets[i] as Program | Episode).seasonNumber as number; // integer or NaN
    episodeNumber = (assets[i] as Program | Episode).episodeNumber as number; // integer or NaN

    seasonEpisodesMap = seasonsEpisodesMap.get(seasonNumber);
    if (!seasonEpisodesMap) {
      seasonEpisodesMap = new Map<number, Asset[]>();
      seasonsEpisodesMap.set(seasonNumber, seasonEpisodesMap);
    }

    episodesArray = seasonEpisodesMap.get(episodeNumber);
    if (!episodesArray) {
      episodesArray = [];
      seasonEpisodesMap.set(episodeNumber, episodesArray);
    }

    // join all assets with the same episode number
    episodesArray.push(assets[i]);
  }

  return seasonsEpisodesMap;
}

function groupAssetsByInternalId(assets: Asset[]): Map<string, Asset[]> {
  const result = new Map<string, Asset[]>();
  let episodesArray: Asset[] | undefined;

  for (let i = 0; i < assets.length; i += 1) {
    episodesArray = result.get((assets[i] as Program | Episode).internalId as string);
    if (!episodesArray) {
      episodesArray = [];
      result.set((assets[i] as Program | Episode).internalId as string, episodesArray);
    }

    // join assets with the same internalId
    episodesArray.push(assets[i]);
  }
  return result;
}

function chooseRepresentativeAsset(clones: Asset[]): Asset {
  clones.sort(
    (assetA, assetB) =>
      ASSET_RELEVANCY_ORDER.indexOf(assetA.mediaType) - ASSET_RELEVANCY_ORDER.indexOf(assetB.mediaType)
  );
  return clones[0];
}

function handleRepresentativeAssetEpisodes(
  seasonsEpisodesMap: Map<number, Map<number, Asset[]>>
): RepresentativeEpisodesResult {
  // for each season group the assets by internalId and select the most representative one
  const episodesBySeason = new Map<number, Asset[]>();
  const episodesClones: Record<string, Asset[]> = {};

  let seasonEpisodes: Asset[];

  let representativeAsset: Asset | undefined;
  seasonsEpisodesMap.forEach((seasonEpisodesMap, seasonNumber) => {
    // init the season asset array, that will contain the distinct assets
    seasonEpisodes = [];
    episodesBySeason.set(seasonNumber, seasonEpisodes);

    seasonEpisodesMap.forEach((assetsWithSameEpisodeNumber) => {
      const assetsByInternalId = groupAssetsByInternalId(assetsWithSameEpisodeNumber);

      assetsByInternalId.forEach((clones, internalId) => {
        representativeAsset = chooseRepresentativeAsset(clones);
        // the episodes title corresponds to the episode name in the seasons rail
        representativeAsset.title = (representativeAsset as Program | Episode).episodeName || representativeAsset.title;
        seasonEpisodes.push(representativeAsset);
        episodesClones[internalId] = clones;
      });
    });
  });

  return {
    episodesBySeason,
    episodesClones,
  };
}

function sortSeasonEpisodes(assets: Asset[]) {
  let episodeNumber1;
  let episodeNumber2;

  let startDate1;
  let startDate2;

  let episodeName1;
  let episodeName2;

  assets.sort((a1, a2) => {
    episodeNumber1 = (a1 as Program | Episode).episodeNumber as number;
    episodeNumber2 = (a2 as Program | Episode).episodeNumber as number;

    if (episodeNumber1 !== episodeNumber2) {
      return episodeNumber1 - episodeNumber2;
    }

    // only programs have startDate
    startDate1 = (a1 as Program).startDate;
    startDate2 = (a2 as Program).startDate;

    if (startDate1 && startDate2) {
      return TimeUtils.compare(startDate1, startDate2);
    }

    episodeName1 = (a1 as Program | Episode).episodeName as string;
    episodeName2 = (a2 as Program | Episode).episodeName as string;

    if (episodeName1 > episodeName2) {
      return 1;
    }
    if (episodeName1 < episodeName2) {
      return -1;
    }

    return 0;
  });
}

function buildAndSortSeasons(assetsBySeason: Map<number, Asset[]>): Record<number, Asset[]> {
  const result: Record<number, Asset[]> = {};

  // sort season numbers ascending
  const seasonNumbersSorted = Array.from(assetsBySeason.keys()).sort((s1, s2) => s1 - s2);

  // special season
  // special season should have the last position
  // removed here because the next step should not consider it
  const specialSeasonIndex = seasonNumbersSorted.findIndex((seasonNumber) =>
    AssetUtils.isSpecialSeasonNumber(seasonNumber)
  );
  if (specialSeasonIndex > -1) {
    seasonNumbersSorted.splice(specialSeasonIndex, 1);
  }

  // invalid season
  // episodes without season are grouped within the lowest (except special season) season number available (if other seasons exists)
  if (assetsBySeason.has(Constants.INVALID_SEASON_NUMBER) && seasonNumbersSorted.length > 1) {
    // the lowest position
    const lowestSeasonAssets: Asset[] = assetsBySeason.values().next().value;
    const invalidSeasonAssets = assetsBySeason.get(Constants.INVALID_SEASON_NUMBER) as Asset[];
    lowestSeasonAssets.push(...invalidSeasonAssets);

    // remove the invalid season data because its assets were merged in the lowest season
    seasonNumbersSorted.splice(
      seasonNumbersSorted.findIndex((seasonNumber) => !AssetUtils.isValidSeasonNumber(seasonNumber)),
      1
    );
    assetsBySeason.delete(Constants.INVALID_SEASON_NUMBER);
  }

  // add special season at the end (removed earlier)
  if (specialSeasonIndex > -1) {
    seasonNumbersSorted.push(AppConfig.env.SPECIAL_SEASON_NUMBER);
  }

  // for each season sort episodes
  // assets with invalid season number are sorted separately
  let validEpisodeNumbers: Asset[];
  let invalidEpisodeNumbers: Asset[];
  let seasonAssets: Asset[];
  seasonNumbersSorted.forEach((seasonNumber) => {
    validEpisodeNumbers = [];
    invalidEpisodeNumbers = [];

    seasonAssets = assetsBySeason.get(seasonNumber) as Asset[];
    seasonAssets.forEach((asset) => {
      if (AssetUtils.hasValidEpisodeNumber(asset)) {
        validEpisodeNumbers.push(asset);
      } else {
        invalidEpisodeNumbers.push(asset);
      }
    });

    sortSeasonEpisodes(invalidEpisodeNumbers);
    sortSeasonEpisodes(validEpisodeNumbers);

    // invalid episodes shall appear first
    const sortedEpisodes = [...invalidEpisodeNumbers, ...validEpisodeNumbers];

    result[seasonNumber] = sortedEpisodes;
  });

  return result;
}

function* getSeriesSeasons(
  entryAsset: Asset,
  entryPoint?: EntryPoint,
  withBookmarks?: boolean
): SagaIterator<SeasonsData> {
  // retrieve the series assets (regarding entryPoint)
  let seriesAssets = yield call(getSeriesAssets, entryAsset, entryPoint);

  if (withBookmarks) {
    yield put(Ducks.BookmarksCache.Creators.loadBookmarks(seriesAssets));
    yield race([take(Ducks.BookmarksCache.ActionTypes.SUCCESS), take(Ducks.BookmarksCache.ActionTypes.FAIL)]);

    seriesAssets = yield select(Ducks.BookmarksCache.Selectors.getAssetsWithProgress, seriesAssets);
  }

  // group assets by season
  // notes:
  // there are assets without season number and/or without episode number
  // there are assets with the same episode number
  // there are assets with the same internalId
  // 1-group assets by season number(even assets without season number)
  const seasonsEpisodesMap = groupAssetsBySeasonsAndEpisodes(seriesAssets);
  // // 2-select the representative asset from the assets with the same internalId
  const representativeEpisodesResult = handleRepresentativeAssetEpisodes(seasonsEpisodesMap);
  // // 3-sort the assets in agreement with the rules
  const episodesBySeason = buildAndSortSeasons(representativeEpisodesResult.episodesBySeason);

  return { episodesBySeason, episodesClones: representativeEpisodesResult.episodesClones };
}

export default {
  getSeriesSeasons,
  getSeasonNumbers,
};
